import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Eye, Flame } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { hr as hrLocale } from 'date-fns/locale';

function timeAgo(date) {
  if (!date) return '';
  try { return formatDistanceToNow(new Date(date), { addSuffix: true, locale: hrLocale }); } catch { return ''; }
}

const CATEGORY_COLORS = {
  'Vijesti': '#1a56db', 'News': '#1a56db',
  'Sport': '#e02424', 'Sports': '#e02424',
  'Gospodarstvo': '#057a55', 'Business': '#057a55',
  'Tehnologija': '#7e3af2', 'Technology': '#7e3af2',
  'Kultura': '#d97706', 'Culture': '#d97706',
  'Zabava': '#ec4899', 'Entertainment': '#ec4899',
  'Zdravlje': '#10b981', 'Health': '#10b981',
  'Putovanja': '#0ea5e9', 'Travel': '#0ea5e9',
  'Politika': '#6366f1', 'Politics': '#6366f1',
  'Crna kronika': '#374151', 'Crime': '#374151',
  'Društvo': '#8b5cf6', 'Society': '#8b5cf6',
  'Aktualno': '#ef4444', 'Current': '#ef4444',
  'EU': '#2563eb',
  'Ekonomija': '#059669', 'Economy': '#059669',
  'Turizam': '#06b6d4', 'Tourism': '#06b6d4',
};

function HeroCard({ article, t }) {
  if (!article) return <div className="skeleton w-full aspect-[16/9] rounded-2xl" />;
  return (
    <article className="group relative rounded-2xl overflow-hidden bg-navy min-h-[380px] lg:min-h-[480px]" data-testid="hero-featured-card">
      {article.featured_image && (
        <img
          src={article.featured_image} alt={article.title}
          loading="eager" fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        {article.is_breaking && (
          <span className="px-2.5 py-1 bg-red-500 text-white text-[10px] font-display font-bold tracking-wider uppercase rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-white rounded-full live-dot" /> BREAKING
          </span>
        )}
        {article.category && (
          <Link to={`/category/${article.category.toLowerCase()}`} className="px-2.5 py-1 bg-sky-accent/90 text-white text-[10px] font-display font-semibold tracking-wider uppercase rounded-full hover:bg-sky-accent transition-colors">
            {article.category}
          </Link>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-8">
        <Link to={`/article/${article.slug}`}>
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3 group-hover:text-sky-accent-light transition-colors line-clamp-3" data-testid="hero-headline">
            {article.title}
          </h1>
        </Link>
        {article.excerpt && (
          <p className="text-gray-300 text-sm lg:text-base line-clamp-2 mb-4 max-w-2xl">{article.excerpt}</p>
        )}
        <div className="flex items-center gap-4">
          <Link to={`/article/${article.slug}`} className="px-5 py-2 bg-sky-accent hover:bg-sky-accent-dark text-white text-sm font-display font-semibold rounded-full transition-colors flex items-center gap-2" data-testid="hero-read-btn">
            {t('read_more')} <ArrowRight size={14} />
          </Link>
          <div className="flex items-center gap-3 text-gray-400 text-xs">
            <span className="flex items-center gap-1"><Clock size={11} /> {timeAgo(article.published_at)}</span>
            {article.view_count > 0 && <span className="flex items-center gap-1"><Eye size={11} /> {article.view_count?.toLocaleString()}</span>}
          </div>
        </div>
      </div>
    </article>
  );
}

function TrendingItem({ article, rank }) {
  return (
    <Link to={`/article/${article.slug}`} className="flex items-start gap-3 group py-2.5 border-b border-gray-100 dark:border-white/5 last:border-0" data-testid={`trending-item-${rank}`}>
      <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-sky-accent to-indigo-accent text-white rounded-lg flex items-center justify-center text-xs font-display font-bold">{rank}</span>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-display font-semibold text-navy dark:text-white leading-snug line-clamp-2 group-hover:text-sky-accent transition-colors">{article.title}</h4>
        <span className="text-[11px] text-gray-400 mt-0.5 block">{timeAgo(article.published_at)}</span>
      </div>
    </Link>
  );
}

export default function HeroSection({ heroArticle, trendingArticles, tags, loading, t }) {
  return (
    <>
      {/* Mobile-only Hero */}
      <section className="md:hidden mb-4" data-testid="mobile-hero-section">
        <div className="mb-4">
          {loading ? <div className="skeleton w-full aspect-[16/9] rounded-2xl" /> : <HeroCard article={heroArticle} t={t} />}
        </div>
        <div className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-4" data-testid="mobile-trending-module">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={14} className="text-sky-accent" />
            <h3 className="font-display text-xs font-bold text-navy dark:text-white tracking-wide uppercase">{t('trending')}</h3>
          </div>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-8 rounded" />)}</div>
          ) : (
            <div>{trendingArticles.slice(0, 4).map((a, i) => <TrendingItem key={a.id || a._id || i} article={a} rank={i + 1} />)}</div>
          )}
        </div>
      </section>

      {/* Desktop Hero + Trending */}
      <section className="hidden md:grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8" data-testid="hero-section">
        <div className="lg:col-span-2">
          {loading ? <div className="skeleton w-full aspect-[16/9] rounded-2xl" /> : <HeroCard article={heroArticle} t={t} />}
        </div>
        <div className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="trending-module">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={16} className="text-sky-accent" />
            <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase">{t('trending')}</h3>
          </div>
          {loading ? (
            <div className="space-y-4">{[1,2,3,4,5].map(i => (
              <div key={i} className="flex gap-3"><div className="skeleton w-7 h-7 rounded-lg flex-shrink-0" /><div className="flex-1 space-y-1.5"><div className="skeleton h-3 w-full rounded" /><div className="skeleton h-2.5 w-2/3 rounded" /></div></div>
            ))}</div>
          ) : (
            <div>{trendingArticles.slice(0, 6).map((a, i) => <TrendingItem key={a.id || a._id || i} article={a} rank={i + 1} />)}</div>
          )}
          {tags.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/5">
              <div className="flex flex-wrap gap-1.5">
                {tags.slice(0, 6).map(tag => (
                  <Link key={tag.name || tag} to={`/tag/${encodeURIComponent(tag.name || tag)}`} className="px-2.5 py-1 bg-sky-accent/5 text-sky-accent text-[10px] font-display font-semibold rounded-full hover:bg-sky-accent/10 transition-colors">{tag.name || tag}</Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export { CATEGORY_COLORS, timeAgo };
